import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  Button,
  type ButtonProps,
  FormControl,
  FormHelperText,
  Grid,
  OutlinedInput,
  Stack,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { type Path, useFormContext, useWatch } from 'react-hook-form';

import BuyerLeadSelect from '@modules/_shared/components/BuyerLeadSelect';
import type { ParentTypeOption } from '@modules/_shared/components/feed/types';
import { type FormFieldDefinitionType } from '@modules/_shared/components/form/RaForm';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { RaSelect } from '@modules/_shared/components/form/RaSelect';
import { LeadSelect } from '@modules/_shared/components/LeadsSelect';
import { LotSelect } from '@modules/_shared/components/LotsSelect';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { type Translate, useLocale } from '@shared/hooks/locale';

type AdvancedFieldsToggleProps = {
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  size?: ButtonProps['size'];
};

export const AdvancedFieldsToggle = ({
  expanded,
  onToggle,
  size = 'small',
}: AdvancedFieldsToggleProps) => {
  const { t } = useLocale();

  return (
    <Button onClick={() => onToggle(!expanded)} variant="text" size={size}>
      {expanded ? t('Show less') : t('Show more')}
    </Button>
  );
};

export type LeadActivityAdvancedFormValues = {
  assigned_to?: string | null;
  contact_ids?: string[];
  created_by?: string | null;
  created_at?: string | null;
  parent_type: ParentTypeOption;
  parent_id: string | null;
  visibility?: 'private' | 'shared' | 'organisation' | null;
};

const ParentTypeField = () => {
  const { t } = useLocale();
  const { control, setValue } =
    useFormContext<LeadActivityAdvancedFormValues>();
  const parentType = useWatch({
    control,
    name: 'parent_type',
  });

  const prevParentTypeRef = useRef<ParentTypeOption | undefined>(undefined);

  // Clear parent_id when parent_type actually changes
  useEffect(() => {
    if (
      prevParentTypeRef.current != null &&
      prevParentTypeRef.current !== parentType
    ) {
      setValue('parent_id', null, { shouldDirty: true });
    }
    prevParentTypeRef.current = parentType;
  }, [parentType, setValue]);

  return (
    <RaSelect
      control={control}
      name="parent_type"
      label={t('Type')}
      options={[
        { value: 'Lead', label: t('Lead') },
        { value: 'Lot', label: t('Listing') },
        { value: 'BuyerLead', label: t('Buyer lead') },
        { value: 'User', label: t('User') },
      ]}
    />
  );
};

const ParentEntityField = () => {
  const { t } = useLocale();
  const { control, setValue } =
    useFormContext<LeadActivityAdvancedFormValues>();
  const [parentType, parentId] = useWatch({
    control,
    name: ['parent_type', 'parent_id'],
  });

  const handleParentChange = useCallback(
    (id: string | null) => {
      setValue('parent_id', id, { shouldDirty: true });
    },
    [setValue],
  );

  // Helper to normalize different onChange signatures
  const handleUserSelectChange = useCallback(
    (value: string | string[] | null) => {
      const id = Array.isArray(value) ? value[0] : value;
      handleParentChange(id);
    },
    [handleParentChange],
  );

  const handleLeadSelectChange = useCallback(
    (lead: { id: string } | null) => {
      handleParentChange(lead?.id ?? null);
    },
    [handleParentChange],
  );

  const selector = useMemo(() => {
    const id = parentId ?? undefined;

    switch (parentType) {
      case 'Lot':
        return <LotSelect lotId={id} onChange={handleParentChange} />;
      case 'BuyerLead':
        return (
          <BuyerLeadSelect buyerLeadId={id} onChange={handleParentChange} />
        );
      case 'User':
        return <UserSelect userId={id} onChange={handleUserSelectChange} />;
      case 'Lead':
        return <LeadSelect leadId={id} onChange={handleLeadSelectChange} />;
      default:
        return <LeadSelect leadId={id} onChange={handleLeadSelectChange} />;
    }
  }, [
    parentType,
    parentId,
    handleParentChange,
    handleUserSelectChange,
    handleLeadSelectChange,
  ]);

  return (
    <Stack direction="column">
      <RaLabel label={t('Parent')} />
      {selector}
    </Stack>
  );
};

const LeadActivityParentFields = () => (
  <Grid container spacing={2}>
    <Grid size={{ xs: 12, sm: 6 }}>
      <ParentTypeField />
    </Grid>
    <Grid size={{ xs: 12, sm: 6 }}>
      <ParentEntityField />
    </Grid>
  </Grid>
);

const normalizeContactIds = (value: string[] | string | null): string[] => {
  if (Array.isArray(value)) {
    return value.filter(item => item != null);
  }
  return value ? [value] : [];
};

const ContactsField = ({
  contactDetails,
}: {
  contactDetails?: string | null;
}) => {
  const { t } = useLocale();
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<LeadActivityAdvancedFormValues>();

  const contactIds = useWatch({
    control,
    name: 'contact_ids',
  });

  const handleChange = useCallback(
    (value: string[] | string | null) => {
      const normalizedIds = normalizeContactIds(value);
      setValue('contact_ids', normalizedIds, { shouldDirty: true });
    },
    [setValue],
  );

  const hasError = Boolean(errors.contact_ids);

  return (
    <FormControl fullWidth error={hasError}>
      <RaLabel label={t('Contact')} />
      <UserSelect multiple userId={contactIds} onChange={handleChange} />
      {hasError ? (
        <FormHelperText>{errors.contact_ids?.message}</FormHelperText>
      ) : contactDetails ? (
        <FormHelperText>{contactDetails}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

export const buildLeadActivityAdvancedFields = <
  TFormData extends LeadActivityAdvancedFormValues,
>({
  expanded,
  t,
  contactDetails,
  showCreatedBy,
  showParentFields,
  showAssigneeField = true,
  showContactField = true,
  showVisibilityField = true,
  createdAt,
}: {
  expanded: boolean;
  t: Translate;
  contactDetails?: string | null;
  showCreatedBy: boolean;
  showParentFields: boolean;
  showAssigneeField?: boolean;
  showContactField?: boolean;
  showVisibilityField?: boolean;
  createdAt?: string | null;
}): FormFieldDefinitionType<TFormData>[] => {
  if (!expanded) {
    return [];
  }

  const mainFields: FormFieldDefinitionType<TFormData>[] = [];

  if (showAssigneeField) {
    mainFields.push({
      type: 'user',
      name: 'assigned_to' as Path<TFormData>,
      label: t('Assigned to'),
      gridProps: { md: 12 },
      filters: {
        _or: [{ is_broker: { _eq: true } }, { is_admin: { _eq: true } }],
      },
    });
  }

  if (showContactField) {
    mainFields.push({
      type: 'custom',
      name: 'contact_ids' as Path<TFormData>,
      element: <ContactsField contactDetails={contactDetails} />,
      gridProps: { md: 12 },
    });
  }

  if (showVisibilityField) {
    mainFields.push({
      type: 'select',
      name: 'visibility' as Path<TFormData>,
      label: t('Visibility'),
      options: () => [
        { value: 'shared', label: t('shared') },
        { value: 'organisation', label: t('Shared within your organisation') },
        { value: 'private', label: t('private') },
      ],
      gridProps: { md: 12 },
    });
  }

  if (showCreatedBy) {
    mainFields.push({
      type: 'user',
      name: 'created_by' as Path<TFormData>,
      label: t('Created by'),
      gridProps: { md: 6 },
    });
    if (createdAt) {
      mainFields.push({
        type: 'custom',
        name: 'created_at',
        gridProps: { md: 6 },
        element: (data: TFormData) => {
          const value = data.created_at;
          const formatted = value ? format(parseISO(value), 'dd/MM/yyyy') : '';
          return (
            <FormControl fullWidth>
              <RaLabel label={t('Created at')} />
              <OutlinedInput
                value={formatted}
                disabled
                size="small"
                fullWidth
                sx={{ background: 'var(--mui-palette-background-paper)' }}
              />
            </FormControl>
          );
        },
      });
    }
  }

  if (showParentFields) {
    mainFields.push({
      type: 'custom',
      name: 'parent_fields',
      element: <LeadActivityParentFields />,
      gridProps: { md: 12 },
    });
  }

  return mainFields;
};
